import type { NodeKind } from '@netdeck/contracts';

/**
 * Типы узлов, на которых бывает VNC-сервер.
 *
 * Список закрытый и короткий: рабочий стол по сети отдаёт машина с рабочим
 * столом. Коммутатору, роутеру, точке доступа, принтеру, ИБП и телефону
 * предлагать консоль незачем — их настраивают по SSH и через веб-интерфейс,
 * а у камеры для этого есть свой раздел с видео.
 *
 * `other` включён намеренно: это «прочее», куда попадает всё, для чего
 * в §7.4 не нашлось типа, — в том числе машины, которые инженер не стал
 * раскладывать по полкам. Отказать им значило бы отобрать консоль
 * у устройства только потому, что его не назвали.
 *
 * `cloud` — облачный сервер — такая же машина с рабочим столом, только
 * арендованная: отличается от `server` глифом, а не возможностями.
 *
 * `label` сюда не входит и не может: подпись на карте — не устройство.
 */
const VNC_CAPABLE_KINDS: ReadonlySet<NodeKind> = new Set<NodeKind>([
  'pc',
  'server',
  'cloud',
  'nas',
  'other',
]);

/**
 * Бывает ли на узле такого типа консоль.
 *
 * Правило одно на всю платформу: панель узла и контекстное меню карты
 * обязаны отвечать одинаково, иначе «Подключиться» пропадает в одном месте
 * и остаётся в другом — и человек решает, что платформа сломалась.
 */
export function canHostVnc(kind: NodeKind): boolean {
  return VNC_CAPABLE_KINDS.has(kind);
}

/**
 * Что интерфейсу нужно знать про VNC на конкретном узле.
 *
 * Собрано в один объект, а не рассыпано отдельными пропсами: панель узла
 * живёт и на карте, и на экране узлов, и обе точки должны давать ей одно
 * и то же — иначе кнопка «Подключиться» появится в одном месте и пропадёт
 * в другом.
 */
export interface NodeVncAccess {
  /**
   * Можно ли подключаться к этому узлу.
   *
   * Функция, а не флаг: право `vnc:connect` бывает и ролевым, и точечным
   * на конкретный узел (§7.6), и ответ зависит от узла.
   */
  canConnect: (nodeId: string) => boolean;
  /** Можно ли менять параметры подключения — это право на узел. */
  canWrite: boolean;
  /** Можно ли привязывать учётные данные — отдельное право `credential:write`. */
  canManageCredentials: boolean;
  /**
   * Открыть консоль на этой машине.
   *
   * Передаётся узел целиком, а не один идентификатор: подписью вкладки
   * служит имя, и добывать его отдельным запросом ради строки на кнопке
   * было бы лишним кругом к серверу.
   */
  onConnect: (node: { id: string; name: string }) => void;
}

/**
 * Право подключаться к узлу: ролевое или точечное (§10.3).
 *
 * Точечные права приходят списком узлов: спрашивать сервер по узлу
 * на каждую карточку карты — это сотня запросов на открытие площадки.
 */
export function canConnectTo(
  rolePermissions: ReadonlySet<string>,
  permittedNodeIds: readonly string[],
  nodeId: string,
): boolean {
  return rolePermissions.has('vnc:connect') || permittedNodeIds.includes(nodeId);
}
